import { createTheme } from "@mui/material/styles";
import {
  ConfiguratorTemplateStyleProps,
  OptionStyleProps,
  OptionsCategoryStyleProps,
  OptionsMenuStyleProps,
  OptionsSubCategoryStyleProps,
  ButtonStyleProps,
  CuratedAngleSelectorStyleProps,
  FinalizeSharePanelStyleProps,
  HorizontalTabsMenuStyleProps,
  PortraitOrientationMessageStyleProps,
  VehicleModelDialogStyleProps,
  VehicleModelTemplateStyleProps,
  View360StyleProps,
  ViewCuratedStyleProps,
} from "./types";

const laptopScreenWidth = 1540;

export const pickSize = (
  mobileSize: string | number,
  tabletSize: string | number,
  desktopSize?: string | number,
  largeScreenSize?: string | number,
): any => ({
  xs: mobileSize,
  sm: tabletSize,
  md: desktopSize ?? tabletSize,
  xl: largeScreenSize ?? tabletSize,
});

export const muiTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 940,
      lg: 1200,
      xl: laptopScreenWidth + 1,
    },
  },
});

const typoBase = {
  h1: 2.5,
  h2: 2.0,
  h3: 1.5,
  h4: 1.25,
  h5: 1.125,
  h6: 0.85,
  mdlNm: 2.2,
  dialogLI: 0.95,
};

function mobileFontSize(value: number) {
  return value - 0.05 + "rem !important";
}

function desktopTabletFontSize(value: number) {
  return value + "rem !important";
}

function largeScreenFontSize(value: number) {
  return value + 0.2 + "rem !important";
}

export const typography = {
  h1: pickSize(
    mobileFontSize(typoBase.h1),
    desktopTabletFontSize(typoBase.h1),
    desktopTabletFontSize(typoBase.h1),
    largeScreenFontSize(typoBase.h1),
  ),
  h2: pickSize(
    mobileFontSize(typoBase.h2),
    desktopTabletFontSize(typoBase.h2),
    desktopTabletFontSize(typoBase.h2),
    largeScreenFontSize(typoBase.h2),
  ),
  h3: pickSize(
    mobileFontSize(typoBase.h3),
    desktopTabletFontSize(typoBase.h3),
    desktopTabletFontSize(typoBase.h3),
    largeScreenFontSize(typoBase.h3),
  ),
  h4: pickSize(
    mobileFontSize(typoBase.h4),
    desktopTabletFontSize(typoBase.h4),
    desktopTabletFontSize(typoBase.h4),
    largeScreenFontSize(typoBase.h4),
  ),
  h5: pickSize(
    mobileFontSize(typoBase.h5),
    desktopTabletFontSize(typoBase.h5),
    desktopTabletFontSize(typoBase.h5),

    largeScreenFontSize(typoBase.h5),
  ),
  h6: pickSize(
    mobileFontSize(typoBase.h6),
    desktopTabletFontSize(typoBase.h6),
    desktopTabletFontSize(typoBase.h6),
    largeScreenFontSize(typoBase.h6),
  ),
  mdlNm: pickSize(
    mobileFontSize(typoBase.mdlNm),
    desktopTabletFontSize(typoBase.mdlNm),
    desktopTabletFontSize(typoBase.mdlNm),
    largeScreenFontSize(typoBase.mdlNm),
  ),
  dialogLI: pickSize(
    mobileFontSize(typoBase.dialogLI),
    desktopTabletFontSize(typoBase.dialogLI),
    desktopTabletFontSize(typoBase.dialogLI),
    largeScreenFontSize(typoBase.dialogLI),
  ),
};

export const optionStyleProps: OptionStyleProps = {
  height: pickSize(
    "4.37vw !important",
    "45px !important",
    "45px !important",
    "55px !important",
  ),
  width: pickSize(
    "4.37vw !important",
    "45px !important",
    "45px !important",
    "55px !important",
  ),
  borderRadius: pickSize(1.5, 2.5, 2.5, 2.5),
  backgroundColor: "lightGray",
  backgroundSize: "104%",
  fontSize: typography.h6,
  border: "1px solid #ffffff00",

  selectedStyle: {
    border: "1px solid #ffffff",
  },
};

export const configuratorTemplateStyleProps: ConfiguratorTemplateStyleProps = {
  height: "100vh",
  width: "100vw",
  optionsMenuStyle: {
    optionsMenuSize: pickSize(
      "23.75vw !important",
      "20% !important",
      "20% !important",
      "20% !important",
    ),
  },
  navigationMenuStyle: {
    top: "3vh",
    left: "0",
    right: pickSize(
      "23% !important",
      "20% !important",
      "20% !important",
      "20% !important",
    ),
    width: "fit-content",
    height: "fit-content",
    margin: "auto",
  },
};

export const optionsMenuStyleProps: OptionsMenuStyleProps = {
  height: "100%",
  width: "100%",
  padding: pickSize("1.7vw !important", "1.5vw !important"),
  boxSizing: "border-box",
  backgroundColor: "rgb(0 0 0 / 20%)",
  backdropFilter: "blur(7px)",
  textAlign: "start",
  titleChildrenSpace: {
    height: pickSize("1.7vw !important", "1.5vw !important"),
  },
  titleStyle: {
    fontSize: pickSize(
      typography.h3.xs,
      typography.h2.s,
      typography.h2.md,
      typography.h2.xl,
    ),
    color: "#ffffff",
    fontWeight: "100",
  },
  subTitleStyle: {
    display: pickSize("none", "block"),
    fontSize: typography.h6,
    color: "#ffffff90",
    fontWeight: "100",
  },
  childrenSpace: {
    space: pickSize(
      "2.5vh !important",
      "2vh !important",
      "2vh !important",
      "2vh !important",
    ),
  },
};

export const optionsCategoryStyleProps: OptionsCategoryStyleProps = {
  borderRadius: "15px",
  padding: pickSize("2vw !important", "2.7vh !important"),
  border: "1px solid #ffffff4f",
  boxSizing: "border-box",
  titleChildrenSpace: {
    height: pickSize("2.5vh !important", "2vh !important"),
  },
  titleStyle: {
    fontSize: typography.h6,
    color: "#ffffff",
    fontWeight: "700",
  },
  subTitleStyle: {
    fontSize: typography.h6,
    color: "#ffffff",
    fontWeight: "100",
  },
  childrenSpace: {
    space: pickSize("2.5vh !important", "2vh !important"),
  },
};

export const optionsSubCategoryStyleProps: OptionsSubCategoryStyleProps = {
  titleChildrenSpace: {
    height: "1vh",
  },
  titleStyle: {
    fontSize: typography.h6,
    color: "#ffffff",
    fontWeight: "700",
  },
  gridStyle: {
    gap: "7px",
  },
};

export const horizontalTabsMenuStyleProps: HorizontalTabsMenuStyleProps = {
  itemGroupStyle: {
    gap: "24px",
    width: "fit-content",
  },
  menuUnderlineStyle: {
    borderBottom: "2px solid #afafaf",
  },
  itemStyle: {
    paddingX: "20px",
    paddingY: "10px",
    color: "#afafaf",
    fontSize: typography.h6,
    fontWeight: 600,
    paddingLeft: "0",
  },
  activeItemStyle: {
    color: "#5c5c5c",
    fontWeight: 600,
  },
  activeUnderlineStyle: {
    height: "2px",
    backgroundColor: "#5c5c5c",
  },
};

export const curatedAngleSelectorStyleProps: CuratedAngleSelectorStyleProps = {
  containerStyle: {
    gap: "14px",
  },
  itemStyle: {
    color: "#ffffff",
    fontSize: typography.h6,
    fontWeight: 500,
  },
  activeItemStyle: {
    color: "#8a8a8a",
    fontWeight: 600,
  },
  separatorStyle: {
    color: "#ffffff",
    fontSize: typography.h6,
    fontWeight: 500,
  },
};

export const viewCuratedStyleProps: ViewCuratedStyleProps = {
  preloaderRootStyle: {
    width: "100vw",
    height: "100vh",
    overflow: "hidden",
    bgcolor: "#b4b4b4",
  },
  preloaderPreviewImageStyle: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    filter: "blur(18px)",
    transform: "scale(1.04)",
    opacity: 0.75,
    backgroundColor: "#cdd0d1",
  },
  preloaderOverlayStyle: {
    backgroundColor: "rgba(255, 255, 255, 0.47)",
  },
  preloaderProgressStyle: {
    fontSize: typography.h6,
    marginRight: pickSize("23.75vw !important", "20% !important"),
  },
  rootStyle: {
    width: "100vw",
    height: "100vh",
    bgcolor: "#000",
    overflow: "hidden",
  },
  angleNavigationStyle: {
    bottom: "5vh",
    right: pickSize("23.75vw !important", "20% !important"),

    left: "0",
    zIndex: 10,
    width: "fit-content",
  },
  arrowButtonStyle: {
    top: "50%",
    color: "#ffffff",
    zIndex: 10,
  },
  leftArrowButtonStyle: {
    left: 15,
  },
  rightArrowButtonStyle: {
    right: pickSize(
      "calc(23.75vw + 15px) !important",
      "calc(20% + 15px) !important",
    ),
  },
  canvasStyle: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
};

export const view360StyleProps: View360StyleProps = {
  preloaderRootStyle: {
    bgcolor: "#cdd0d1",
  },
  preloaderPreviewImageStyle: {
    objectFit: "contain !important",
    "@media (min-width:600px) and (min-height:600px) and (max-width:1199px)": {
      objectFit: "contain !important",
    },
    backgroundColor: "#cdd0d1",
    // filter: "blur(18px)",
    // transform: "scale(1.04)",
    opacity: 0.75,
  },
  preloaderOverlayStyle: {
    backgroundColor: "rgba(0, 0, 0, 0.25)",
  },
  preloaderProgressStyle: {
    fontSize: typography.h6,
  },
  rootStyle: {
    bgcolor: "#cdd0d1",
  },
  canvasStyle: {
    objectFit: "contain !important",
    "@media (min-width:600px) and (min-height:600px) and (max-width:1199px)": {
      objectFit: "contain !important",
    },
    backgroundColor: "#cdd0d1",
    touchAction: "none",
  },
  canvasGrabCursor: "grab",
  canvasGrabbingCursor: "grabbing",
};

export const vehicleModelTemplateStyleProps: VehicleModelTemplateStyleProps = {
  templateStyle: {
    display: "flex",
    height: "100vh",
    width: "100vw",
    overflow: "hidden",
    backgroundImage: "url('/assets/Models/background.png')",
    backgroundSize: "cover",
    backgroundPosition: "center",
  },
  gradiantLayer: {
    pointerEvents: "none",
    position: "absolute",
    height: "100vh",
    width: "100vw",
    overflow: "hidden",
    // background:
    //   "linear-gradient(2deg, #00000029, transparent, transparent, transparent, transparent)",
  },

  modelBoxStyle: {
    position: "relative",
    flex: 1,
    minWidth: 0,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "opacity 0.3s",
    opacity: 0.7,
    backgroundSize: "95% !important",
    backgroundPosition: "center center !important",
    backgroundRepeat: "no-repeat",
    // background: "linear-gradient(0deg, #bdbdbd, #bdbdbd, #fafafa, #fafafa);",
    gap: "3vh",
    "&:hover": {
      opacity: 1,
    },
  },
  modelSelectedStyle: {
    opacity: 1,
  },
  logoContainerStyle: {
    minHeight: "7vh",
    position: "absolute",
    bottom: "13vh",
  },
  logoStyle: {
    width: "30vw",
  },
  actionsStyle: {
    opacity: 0,
    position: "absolute",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "opacity 0.3s",
    bottom: "3vh",
    gap: "14px",
  },
  actionsVisibleStyle: {
    opacity: 1,
  },
};

export const buttonStyleProps: ButtonStyleProps = {
  borderRadius: "4px",
  transition: "all 0.3s",
  backgroundImage: "url(/assets/button-outlined.svg)",
  color: "#ffffff",
  fontSize: typography.h6,
  fontWeight: 700,
  letterSpacing: 0,
  paddingLeft: "20px",
  paddingRight: "40px",
  paddingTop: pickSize("14px", "15px"),
  paddingBottom: pickSize("16px", "19px"),
  backgroundSize: "100%",
  backgroundRepeat: "no-repeat",
  width: "fit-content",
  margin: "auto",
  zIndex: "99",
  "&:hover": {
    backgroundImage: "url(/assets/button-filled.svg)",
    color: "#000000",
  },
};

export const vehicleModelDialogStyleProps: VehicleModelDialogStyleProps = {
  dialogPaperStyle: {
    borderRadius: "6px",
    backgroundColor: "#111318",
    color: "#ffffff",
  },
  dialogTitleStyle: {
    fontWeight: 700,
    letterSpacing: 0,
  },
  dialogListStyle: {
    m: 0,
    pl: "1.2rem",
  },
  dialogListItemStyle: {
    mb: 1,
    color: "#d6dae1",
    fontSize: typography.dialogLI,
    lineHeight: 1.5,
  },
};

export const portraitOrientationMessageStyleProps: PortraitOrientationMessageStyleProps =
  {
    rootStyle: {
      minHeight: "100vh",
      width: "100vw",
      backgroundColor: "#222222",
      color: "#ffffff",
      px: pickSize("28px !important", "40px !important"),
    },
    contentStyle: {
      width: "100%",
      maxWidth: "420px",
    },
    iconContainerStyle: {
      width: "90px !important",
      height: "90px !important",
      border: "1px solid rgba(255,255,255,0.34)",
      color: "#ffffff",
    },
    iconStyle: {
      fontSize: "2rem",
    },
    titleStyle: {
      color: "#ffffff",
      fontSize: typography.h3,
      fontWeight: 700,
    },
    bodyStyle: {
      color: "rgba(255,255,255,0.72)",
      fontSize: typography.h6,
    },
  };

export const finalizeSharePanelStyleProps: FinalizeSharePanelStyleProps = {
  rootStyle: {
    // minHeight: "100%",
    width: "100%",
  },
  imageStackStyle: {
    width: "100%",
    height: pickSize("20vh !important", "23vh !important"),
  },
  imageFrameStyle: {
    width: pickSize("14vw !important", "14vw !important"),
    aspectRatio: "16 / 9",
    borderRadius: "3px",
    border: "1px solid rgba(255, 255, 255, 0.06)",
  },
  imageStyle: {
    width: "100%",
    height: "100%",
    backgroundColor: "#111318",
  },
  buttonWrapStyle: {
    width: "100%",
  },
};
